import { FormattedMessage, type IntlShape } from 'react-intl';
import { getCopyrightText } from '@alltrails/core/utils/getCopyrightText';
import Link from '@alltrails/denali/components/Link';
import Logo from '@alltrails/denali/components/Logo';
import PeakLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Peak';
import PlusLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Plus';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import type { User } from '@alltrails/types/User';
import AppForOutdoors from './AppForTheOutdoors';
import ConnectWithUs from './ConnectWithUs';
import styles from './Footer.module.scss';
import FooterLinks from './FooterLinks';
import LanguageSelectDropdown from './LanguageSelectDropdown';
import ManageCookies from './ManageCookies';
import MembersForThePlanet from './MembersForThePlanet';

const enabledLanguages = [
  'en-US',
  'en-GB',
  'da-DK',
  'de-DE',
  'es-ES',
  'es-MX',
  'fr-FR',
  'it-IT',
  'nl-NL',
  'nb-NO',
  'pl-PL',
  'pt-BR',
  'pt-PT',
  'sv-SE'
];

export type Props = {
  user?: User | null;
  wideFooter: boolean;
  languageRegionCode: LanguageRegionCode;
  noFollowSocials?: boolean;
  noReferrerAppLinks?: boolean;
  irclickid?: string;
  intl: IntlShape;
  pageReloadOnLanguageChange?: boolean;
};

const Footer = ({
  user,
  wideFooter,
  languageRegionCode,
  noFollowSocials,
  noReferrerAppLinks,
  irclickid,
  intl,
  pageReloadOnLanguageChange
}: Props) => {
  return (
    <footer className={`${styles.container} ${wideFooter ? styles.wideContainer : ''}`}>
      <div className={`${styles.innerContainer} ${wideFooter ? styles.wideInnerContainer : ''}`}>
        <Logo size="sm" color="colored-light" variant="logomark" />
        <FooterLinks intl={intl} languageRegionCode={languageRegionCode} user={user} irclickid={irclickid} />
        <div className={styles.panelsContainer}>
          <div className={styles.appForOutdoors}>
            <AppForOutdoors languageRegionCode={languageRegionCode} noReferrerAppLinks={noReferrerAppLinks} intl={intl} />
          </div>
          <div className={styles.membersContainer}>
            <MembersForThePlanet intl={intl} />
          </div>
          <div className={styles.connectWithUs}>
            <ConnectWithUs noFollowSocials={noFollowSocials} intl={intl} />
          </div>
        </div>
        <div className={styles.bottomText}>
          <div className={styles.footNotes}>
            <div>
              <div className={styles.copyright}>{getCopyrightText({ intl })}</div>
              <div>
                {intl.formatMessage({
                  defaultMessage:
                    'ALLTRAILS® and the AllTrails Mountain Design are registered trademarks of AllTrails, LLC in the United States as well as certain other jurisdictions.'
                })}
              </div>
              <div>
                <FormattedMessage
                  defaultMessage="FIND YOUR OUTSIDE™, {plusIcon} PLUS™, and {peakIcon} PEAK™ are trademarks of AllTrails, LLC."
                  values={{
                    peakIcon: <PeakLogoBadge className={styles.trademarkBadge} size="sm" variant="icon" />,
                    plusIcon: <PlusLogoBadge className={styles.trademarkBadge} size="sm" variant="icon" />
                  }}
                />
              </div>
            </div>
          </div>
          <div className={styles.languageSelect}>
            <LanguageSelectDropdown
              languageRegionCode={languageRegionCode}
              enabledLanguages={enabledLanguages}
              pageReloadOnLanguageChange={pageReloadOnLanguageChange}
            />
          </div>
        </div>
        <div className={styles.userLinks}>
          <Link href="/privacy" noUnderline rel="nofollow" size="sm" variant="inverse">
            {intl.formatMessage({ defaultMessage: 'Privacy Policy' })}
          </Link>
          •
          <Link href="/terms" noUnderline rel="nofollow" size="sm" variant="inverse">
            {intl.formatMessage({ defaultMessage: 'Terms' })}
          </Link>
          •
          <Link href="/cookie_policy" noUnderline rel="nofollow" size="sm" variant="inverse">
            {intl.formatMessage({ defaultMessage: 'Cookie Policy' })}
          </Link>
          <ManageCookies />
        </div>
      </div>
    </footer>
  );
};

export default Footer;
